/**
 * CLE-2811 — "What it is looking for": the things this pass exists to find, each ticked the first time
 * the AI's narration mentions it. A tick is a sign of where the AI has got to, not a verdict on what it
 * found — the model page is where that is checked.
 */

import React from "react";
import styled, { css } from "styled-components";

import { LOOKING_FOR_HEADING, LOOKING_FOR_HINT } from "../copy";
import { IChecklistItem } from "./progressModel";
import { GREEN, SectionTitle, fadeIn } from "./styles";

const Items = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(13rem, 1fr));
  gap: 0.4rem 1rem;
`;

const Item = styled.li<{ $ticked: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: ${({ $ticked }) =>
    $ticked ? "var(--text-color)" : "var(--text-color-secondary)"};

  i {
    font-size: 0.95rem;
    color: ${({ $ticked }) => ($ticked ? GREEN : "var(--surface-400)")};
    ${({ $ticked }) =>
      $ticked &&
      css`
        animation: ${fadeIn} 0.4s ease-out;
      `}
  }
`;

interface ILookingForChecklistProps {
  items: IChecklistItem[];
  ticked: string[];
}

function LookingForChecklist({ items, ticked }: ILookingForChecklistProps) {
  const hit = new Set(ticked);
  return (
    <section>
      <SectionTitle>
        {LOOKING_FOR_HEADING}
        <span className="font-normal text-color-secondary text-xs">
          {hit.size} of {items.length}
        </span>
      </SectionTitle>
      <Items>
        {items.map((item) => {
          const on = hit.has(item.id);
          return (
            <Item key={item.id} $ticked={on}>
              <i className={on ? "pi pi-check-circle" : "pi pi-circle"} />
              {item.label}
            </Item>
          );
        })}
      </Items>
      <p className="text-xs text-color-secondary mt-2 mb-0">
        {LOOKING_FOR_HINT}
      </p>
    </section>
  );
}

export default LookingForChecklist;
